import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PlusIcon, SearchIcon, CashIcon } from "@heroicons/react/outline";
import Table from "../../../../Components/v2/Table";
import DateTimeCell from "../../../../Components/v2/Table/DateTimeCell";
import TableActions from "../../../../Components/v2/Table/TableActions";
import Pagination from "../../../../Components/v2/Pagination";
import Button from "../../../../Components/v2/Button";
import DepositStatusBadge from "../../../../Components/v2/Accounting/DepositStatusBadge";
import DepositModal from "../../../../Components/v2/Accounting/DepositModal";
import DepositDetailModal from "../../../../Components/v2/Accounting/DepositDetailModal";
import { ModalAlert } from "../../../../Components/ModalAlert";
import {
  addLocalNotification,
  deleteDeposit,
  useNotificationDispatch,
} from "../../../../Context";
import { formatCurrency } from "../../../../Utils";
import {
  DEPOSIT_STATUS_ORDER,
  getDepositActionError,
  isDepositOpen,
} from "../../../../Utils/DepositUtil";

// A deposit counts as used once anything has come off it; only an untouched one
// can still be deleted or edited.
const isUsed = (deposit) =>
  deposit.status !== "OPEN" ||
  Math.round(deposit.remaining || 0) < Math.round(deposit.total || 0);

/**
 * W1 · the Uang Muka tab. The page loads the deposits (its tab badge needs the
 * count too) and this tab filters, searches and pages them on the client: the
 * endpoint returns the whole list in one go.
 */
const DepositsTab = ({
  businessID,
  deposits = [],
  summary,
  loading,
  error,
  onReload,
  partnerFilter,
  onClearPartnerFilter,
  openDepositID,
  onDepositOpened,
}) => {
  const { t } = useTranslation();
  const notificationDispatch = useNotificationDispatch();

  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [addOpen, setAddOpen] = useState(false);
  const [detail, setDetail] = useState(null); // { id, view }
  const [toDelete, setToDelete] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // A link from elsewhere (Rekap PPN, a transaction row) names one deposit.
  useEffect(() => {
    if (openDepositID) {
      setDetail({ id: openDepositID, view: "detail" });
      onDepositOpened?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDepositID]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return deposits.filter((deposit) => {
      if (partnerFilter?.id && deposit.partner?.id !== partnerFilter.id)
        return false;
      if (status === "open" && !isDepositOpen(deposit)) return false;
      if (status && status !== "open" && deposit.status !== status)
        return false;
      if (!query) return true;
      return (
        deposit.depositNumber?.toLowerCase().includes(query) ||
        deposit.partner?.name?.toLowerCase().includes(query)
      );
    });
  }, [deposits, status, search, partnerFilter]);

  useEffect(() => setPage(1), [status, search, partnerFilter, perPage]);

  const pageItems = filtered.slice((page - 1) * perPage, page * perPage);

  const confirmDelete = async () => {
    if (!toDelete || deleteBusy) return;
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await deleteDeposit(toDelete.id);
      setDeleteBusy(false);
      setToDelete(null);
      addLocalNotification(notificationDispatch, {
        id: `delete-deposit-${toDelete.id}`,
        type: "success",
        title: "common.success",
        body: "deposits.notifications.deleted",
        showFull: true,
      });
      onReload();
    } catch (err) {
      setDeleteBusy(false);
      setDeleteError(getDepositActionError(err, t));
    }
  };

  const statusOptions = [
    { value: "", label: t("deposits.status.all") },
    { value: "open", label: t("deposits.status.open") },
    ...DEPOSIT_STATUS_ORDER.map((value) => ({
      value,
      label: t(`deposits.status.${value}`),
    })),
  ];

  const isEmpty = !loading && !error && deposits.length === 0;

  return (
    <div className="px-4 py-5 sm:px-6 lg:px-8">
      <div className="mb-4 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">
            {t("deposits.summary.remaining")}
          </p>
          <p
            className="text-2xl font-semibold tabular-nums text-gray-900 [overflow-wrap:anywhere]"
            data-testid="deposit-remaining"
          >
            {formatCurrency(summary?.remaining || 0)}
          </p>
          <p className="text-sm text-gray-500">
            {t("deposits.summary.remainingFrom", {
              interpolation: { escapeValue: false },
              count: summary?.count || 0,
            })}
          </p>
        </div>
        <Button
          variant="secondary"
          color="blue"
          leadingIcon={PlusIcon}
          onClick={() => setAddOpen(true)}
        >
          {t("deposits.actions.add")}
        </Button>
      </div>

      {isEmpty ? (
        <div className="rounded-lg border border-dashed border-gray-300 px-6 py-12 text-center">
          <CashIcon
            className="mx-auto h-10 w-10 text-gray-400"
            aria-hidden="true"
          />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">
            {t("deposits.empty.title")}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {t("deposits.empty.body")}
          </p>
          <Button
            className="mt-4"
            leadingIcon={PlusIcon}
            onClick={() => setAddOpen(true)}
          >
            {t("deposits.actions.add")}
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <SearchIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                type="search"
                aria-label={t("deposits.placeholders.search")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("deposits.placeholders.search")}
                className="block w-full rounded-md border-0 py-2 pl-10 text-sm shadow-sm ring-1 ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500"
              />
            </div>
            <select
              aria-label={t("deposits.fields.status")}
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="block w-full rounded-md border-gray-300 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {partnerFilter?.id && (
            <div className="mb-4 flex items-center gap-2 text-sm">
              <span className="rounded-full bg-blue-50 px-3 py-1 text-blue-800">
                {t("deposits.fields.customer")}: {partnerFilter.name || "-"}
              </span>
              <button
                type="button"
                onClick={onClearPartnerFilter}
                className="text-gray-500 hover:text-gray-700 hover:underline"
              >
                {t("common.clear")}
              </button>
            </div>
          )}

          {!loading && !error && filtered.length === 0 ? (
            <p className="rounded-lg border border-gray-200 px-4 py-8 text-center text-sm text-gray-500">
              {t("deposits.empty.noMatch")}
            </p>
          ) : (
            <Table
              isLoading={loading}
              error={error}
              onRetry={onReload}
              data={pageItems}
              onRowClick={(deposit) =>
                setDetail({ id: deposit.id, view: "detail" })
              }
              columns={[
                {
                  header: "deposits.fields.date",
                  accessor: (deposit) => (
                    <DateTimeCell value={deposit.createdAt} />
                  ),
                },
                {
                  header: "deposits.fields.number",
                  accessor: (deposit) => (
                    <span className="font-medium text-gray-900">
                      {deposit.depositNumber}
                    </span>
                  ),
                },
                {
                  header: "deposits.fields.customer",
                  accessor: (deposit) => (
                    <span className="block max-w-[12rem] truncate">
                      {deposit.partner?.name || "-"}
                    </span>
                  ),
                },
                {
                  header: "deposits.fields.received",
                  align: "right",
                  accessor: (deposit) => (
                    <span className="tabular-nums">
                      {formatCurrency(deposit.total)}
                    </span>
                  ),
                },
                {
                  header: "deposits.fields.remaining",
                  align: "right",
                  accessor: (deposit) => (
                    <span className="font-medium tabular-nums">
                      {formatCurrency(deposit.remaining)}
                    </span>
                  ),
                },
                {
                  header: "deposits.fields.status",
                  accessor: (deposit) => (
                    <DepositStatusBadge status={deposit.status} />
                  ),
                },
                {
                  header: "common.actions.label",
                  align: "right",
                  accessor: (deposit, index) => (
                    // Keeps the menu's clicks from also opening the row.
                    <div
                      className="inline-block"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <TableActions
                        label={`${t("common.actions.label")} ${deposit.depositNumber}`}
                        position={
                          index === pageItems.length - 1 ? "top" : "bottom"
                        }
                        actions={[
                          {
                            label: t("deposits.actions.view"),
                            onClick: () =>
                              setDetail({ id: deposit.id, view: "detail" }),
                          },
                          {
                            label: t("deposits.actions.apply"),
                            onClick: () =>
                              setDetail({ id: deposit.id, view: "apply" }),
                            disabled: !isDepositOpen(deposit),
                            tooltip: !isDepositOpen(deposit)
                              ? t("deposits.disabled.nothingLeft")
                              : undefined,
                          },
                          {
                            label: t("deposits.actions.delete"),
                            variant: "danger",
                            onClick: () => {
                              setDeleteError("");
                              setToDelete(deposit);
                            },
                            disabled: isUsed(deposit),
                            tooltip: isUsed(deposit)
                              ? t("deposits.disabled.usedDelete")
                              : undefined,
                          },
                        ]}
                      />
                    </div>
                  ),
                },
              ]}
            />
          )}

          {filtered.length > perPage && (
            <Pagination
              isOffline
              items={filtered}
              currentPage={page}
              perPage={perPage}
              isLoading={loading}
              onPageChange={(next) => setPage(next)}
              onPerPageChange={setPerPage}
            />
          )}
        </>
      )}

      {addOpen && (
        <DepositModal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          businessID={businessID}
          partner={partnerFilter?.id ? partnerFilter : null}
          onSaved={(result) => {
            setAddOpen(false);
            addLocalNotification(notificationDispatch, {
              id: `create-deposit-${result?.id || Date.now()}`,
              type: "success",
              title: "common.success",
              body: "deposits.notifications.created",
              showFull: true,
            });
            onReload();
          }}
        />
      )}

      {detail && (
        <DepositDetailModal
          open={Boolean(detail)}
          depositID={detail.id}
          initialView={detail.view}
          businessID={businessID}
          onClose={() => setDetail(null)}
          onChanged={onReload}
        />
      )}

      <ModalAlert
        open={Boolean(toDelete)}
        setOpen={(value) => !value && setToDelete(null)}
        title="deposits.delete.title"
        descriptionNode={
          toDelete ? (
            <>
              {t("deposits.delete.body", {
                interpolation: { escapeValue: false },
                number: toDelete.depositNumber,
                partner: toDelete.partner?.name || "-",
                amount: formatCurrency(toDelete.total),
              })}
              {deleteError && (
                <span
                  role="alert"
                  className="mt-2 block font-medium text-red-600"
                >
                  {deleteError}
                </span>
              )}
            </>
          ) : null
        }
        confirmButtonText="common.delete"
        cancelButtonText="common.cancel"
        loadingButton={deleteBusy}
        disabledButton={deleteBusy}
        onConfirm={confirmDelete}
        onCancel={() => setToDelete(null)}
      />
    </div>
  );
};

export default DepositsTab;
